
































import Vue from 'vue'
import { debounce } from 'debounce'
import { mapGetters } from 'vuex'
import mediaMixin from '@/mixins/media-mixin'
import Button from '@/types/components/Button.interface'
import IListIcon, { IListIconType } from '@/types/components/IListIcon.interface'
import IListTile from '@/types/components/IListTile.interface'
import { MediaFilterParams } from '@/types/FilterParams.interface'
import Media from '@/types/Media.interface'
import ProgressBar from '@/components/ProgressBar.vue'
import ScrollPosition from '@/types/components/ScrollPosition.interface'
export default Vue.extend({
    mixins: [mediaMixin],
    components: { ProgressBar },
    data() {
        return {
            //  Determines if we are showing the filter list or not
            filtered: false,
            //  Our temporary groups list
            filterResults: null,
            //  A loader for searching
            loading: false,
            //  Our placeholder for our media list
            mediaListPlaceholder: {
                img: 'noDocuments',
                content: 'No Files to Display'
            },
            //  Determines if we are showing the search results list or not
            searched: false,
            //  Our temporary groups list
            searchResults: null,
            //  Our container for our search text
            searchtext: '',
            //  Our toolbar displayed at the top
            toolbar: {
                title: 'Media',
                left: [
                    {
                        title: 'Back',
                        icon: 'mdi-chevron-left',
                        back: '/home',
                        tooltip: 'Back',
                        permission: 0
                    }
                ]
            }
        }
    },
    mounted() {
        //  Grab our store media
        const media = this.$store.getters.mediaViewMedia

        //  If we don't have media, then paginate
        if (!media || !media.length) this.$store.dispatch('mediaViewMediaPaginate')
    },
    computed: {
        ...mapGetters({
            defaultParams: 'mediaViewMediaDefaultParams',
            folders: 'mediaViewFolders',
            paginating: 'mediaViewMediaPaginating',
            scrollPositionLeft: 'scrollPositionLeft'
        }),
        media(): Media[] {
            //  If we have filtered, then display our filtered list
            if (this.filtered) return this.filterResults
            //  If we are searching then display our temporary list
            else if (this.searched) return this.searchResults
            //  Else, just display the mediaViewMedia
            return this.$store.getters.mediaViewMedia
        },
        mediaParams: {
            get(): MediaFilterParams {
                return this.$store.getters.mediaViewMediaParams
            },
            set(val: MediaFilterParams): void {
                this.$store.commit('mediaViewMediaParamsSet', val)
            }
        },
        progressText(): string {
            //  Get our storage used
            const used = this.$options.filters.fileSize(this.storageUsed)

            //  Get our storage limit
            const limit = this.$options.filters.fileSize(this.storageLimit)

            return `Media storage: ${used} / ${limit}`
        },
        search: {
            get(): string {
                return this.searchtext
            },
            set: debounce(function (val: string): void {
                //  Set the searchtext state
                this.searchtext = val
                this.mediaSearch()
            }, 750)
        },
        tools(): Button[] {
            return [
                {
                    text: 'New File',
                    icon: 'mdi-plus',
                    color: this.$colors.mdPurple,
                    dark: true,
                    small: true,
                    callback: () => this.goToMediaUpload()
                }
            ]
        }
    },
    methods: {
        folderIcon(): IListIcon {
            //  Our icon is always a folder
            const icon = 'mdi-folder'

            //  Create our list icon
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: 'primary'
            }

            //  Return our list icon
            return listIcon
        },
        folderTile(): IListTile {
            return {
                leftIcon: (): IListIcon => this.folderIcon(),
                leftHeader: 'Folder',
                line1: (folder: string): string => folder.toProperCase(),
                callback: (folder: string): void => this.goToFolder(folder)
            }
        },
        goToFolder(folder: string): void {
            //  Go to the media folder
            this.$router.push(`/media/folder/${folder}`)
        },
        async mediaSearch(): Promise<void> {
            //  If search text is null then reset
            if (!this.searchtext) {
                //  Set to false so we display regular list
                this.searched = false
                //  Set to null to get rid of our previous list of media
                return (this.searchResults = null)
            }

            //  Else, hit up the API to search
            const text = this.searchtext

            //  Create our payload
            const payload = {
                searchtext: text,
                filter: this.mediaParams.filter
            }

            //  Start a loader
            this.loading = true

            //  Get our search results
            const results = await this.$store.dispatch('mediaSearch', payload)

            //  Stop our loader
            this.loading = false

            //  Set our search results
            this.searchResults = results || []

            //  Regardless of result, we searched so display the list
            this.searched = true
        },
        onFilterChanged(filter: MediaFilterParams): void {
            //  Grab the key and value from our filter
            const key = filter.key
            const val = filter.value

            //  Get our current media params
            const params = this.mediaParams

            //  Check if we currently have this filter applied
            const isSame = params[key] === val

            //  If it is the same, do nothing
            if (isSame) return

            //  Our "otherKey" is "filter" if we are sorting, "sort" if we are filtering
            const otherKey = key === 'filter' ? 'sort' : 'filter'

            //  If our other key exists, set it to null
            if (params[otherKey]) params[otherKey] = null

            //  Else, apply this filter to our params
            params[key] = val

            //  Set page to 1 since we changed filters
            params.page = 1

            //  Set our params (we have a computed setter to update Vuex Store)
            this.mediaParams = params

            //  We changed our filter so lets paginate again
            this.$store.dispatch('mediaViewMediaPaginate')
        },
        paginateCurrentList(): void {
            //  Grab our current page
            const current = this.$store.getters.mediaViewMediaCurrentPage

            //  Grab the last page
            const lastPage = this.$store.getters.mediaViewMediaLastPage

            //  Determine the next page
            const nextPage = current + 1

            //  Determine is the page exists, or we're already at the end of pagination
            const shouldPaginate = nextPage <= lastPage

            //  If we should NOT paginate, do nothing
            if (!shouldPaginate) return

            //  Paginate the next page
            this.$store.dispatch('mediaViewMediaPaginate', nextPage)
        }
    },
    watch: {
        scrollPositionLeft(position: ScrollPosition): void {
            //  If we are NOT paginating AND scroll is at bottom, paginate next page
            if (!this.paginating && position.bottom) this.paginateCurrentList()
        }
    }
})
